import { ExpressionSpecification, Map as MlMap } from "maplibre-gl";
import { DemPack, getDemPacks } from "../terrain/dem-packs";
import { ensureDemSources, sourceIdFor } from "./webgl-maplibre.dem-source";
import { firstLayerAboveBasemap } from "./webgl-maplibre.layers";

/**
 * Terrain tints over the DEM packs: the ceiling overlay (terrain above the
 * configured ceiling) and the terrain overlay (terrain near or above the
 * aircraft). Each overlay is ONE layer on ONE pack, picked per view by
 * `pickReliefPack`. One layer per pack, as hillshade does it, stacked the
 * translucent tints where packs overlap: two edges and a darker band.
 *
 * Painted with MapLibre's own `color-relief` layer type (5.6+), which ramps a
 * colour over the DEM's decoded elevation (metres) on the GPU. navpad's
 * MapLibre "ceiling layer" is NOT a reference implementation: it is a plain
 * 0.6-opacity DEM raster that never reacts to the limit. The reference is
 * navpad's older OpenLayers map (KB: concepts/terrain-ceiling-overlays.md).
 *
 * Both tints go directly above the basemap rasters, so they colour only the
 * terrain: never the route, obstacles, icons or labels.
 */

const TINT_ALPHA = 0.6;

/** Blue above the ceiling, deepening with height. Limit in metres MSL. */
export function ceilingColor(ceilingM: number): ExpressionSpecification {
  // The 1 m step below the limit keeps the boundary a hard edge instead of a
  // gradient that starts at sea level.
  return [
    "interpolate",
    ["linear"],
    ["elevation"],
    Math.max(0, ceilingM - 1),
    "rgba(0, 0, 0, 0)",
    ceilingM,
    "rgba(0, 96, 214, 0.45)",
    ceilingM + 1500,
    "rgba(0, 40, 140, 0.7)",
  ];
}

/** Clearance limits of the terrain bands (clearance = aircraft altitude - terrain), in metres. */
export const TERRAIN_RED_CLEARANCE_M = 100 * 0.3048;
export const TERRAIN_YELLOW_CLEARANCE_M = 1000 * 0.3048;
export const TERRAIN_GREEN_CLEARANCE_M = 2000 * 0.3048;

/** Above all terrain, so nothing is tinted. */
const NO_FIX_ALTITUDE_M = 100000;

/**
 * Red where the clearance is 100 ft or less (terrain above the aircraft too),
 * yellow below 1000 ft, green below 2000 ft, no tint otherwise. Altitude in
 * metres MSL; `null` tints nothing, instead of painting all terrain red.
 * `TerrainReference` passes `null` on the device when the vertical accuracy of
 * the GPS fix is poor. With no fix it passes 2500 ft, not `null`.
 */
export function terrainColor(fixAltitudeM: number | null): ExpressionSpecification {
  const altitudeM = fixAltitudeM ?? NO_FIX_ALTITUDE_M;
  const greenM = altitudeM - TERRAIN_GREEN_CLEARANCE_M;
  const yellowM = altitudeM - TERRAIN_YELLOW_CLEARANCE_M;
  const redM = altitudeM - TERRAIN_RED_CLEARANCE_M;
  const green = `rgba(0, 170, 0, ${TINT_ALPHA})`;
  const yellow = `rgba(255, 204, 0, ${TINT_ALPHA})`;
  // The 1 m steps keep each band boundary a hard edge.
  return [
    "interpolate",
    ["linear"],
    ["elevation"],
    greenM - 1,
    "rgba(0, 0, 0, 0)",
    greenM,
    green,
    yellowM - 1,
    green,
    yellowM,
    yellow,
    redM - 1,
    yellow,
    redM,
    `rgba(214, 0, 0, ${TINT_ALPHA})`,
  ];
}

/**
 * The 30 m pack when it can paint the whole view, else the broad 90 m pack.
 * A regional pack needs the zoom to reach its `min_zoom` (no tiles below) and
 * its bbox to hold the whole view, so no part of the view goes untinted.
 */
export function pickReliefPack(packs: DemPack[], view: [number, number, number, number], zoom: number): DemPack | null {
  const [w, s, e, n] = view;
  const detailed = packs
    .filter((p) => {
      const [pw, ps, pe, pn] = p.manifest.bbox;
      return zoom >= p.manifest.min_zoom && w >= pw && s >= ps && e <= pe && n <= pn;
    })
    .sort((a, b) => b.manifest.priority - a.manifest.priority);
  if (detailed.length > 0) return detailed[0];
  return [...packs].sort((a, b) => a.manifest.min_zoom - b.manifest.min_zoom)[0] ?? null;
}

export async function hasReliefPacks(): Promise<boolean> {
  return (await getDemPacks()).length > 0;
}

export class ReliefOverlay {
  private layerId: string | null = null;
  private packs: DemPack[] = [];
  /** Serializes show/hide, so a hide during a slow show cannot leave the tint on. */
  private queue: Promise<void> = Promise.resolve();
  private color: ExpressionSpecification | null = null;
  // The ramp textures in use before the last color change (see setColor).
  private staleRamp: { elevationTexture?: { destroy(): void }; colorTexture?: { destroy(): void } } | null = null;
  private onMoveEnd: (() => void) | null = null;

  constructor(private readonly prefix: string) {}

  /** The map was destroyed: forget its layer and listener. */
  reset(): void {
    this.layerId = null;
    this.packs = [];
    this.queue = Promise.resolve();
    this.color = null;
    this.staleRamp = null;
    this.onMoveEnd = null;
  }

  show(map: MlMap, color: ExpressionSpecification): Promise<void> {
    this.color = color;
    return this.enqueue(() => this.install(map));
  }

  hide(map: MlMap): Promise<void> {
    return this.enqueue(async () => this.remove(map));
  }

  /** Changes the ramp in place. Called on every position update. */
  setColor(map: MlMap, color: ExpressionSpecification): void {
    if (JSON.stringify(color) === JSON.stringify(this.color)) return;
    this.color = color;
    const layer = this.layerId ? (map.getLayer(this.layerId) as any) : undefined;
    if (!layer) return;
    // MapLibre 6.10 makes two new ramp textures on the render after each change and never
    // destroys the old ones (ColorReliefStyleLayer.getColorRampTextures). Free the old pair once
    // the layer has replaced it; the layer still draws with it until then.
    const current = layer.colorRampTextures;
    if (this.staleRamp && this.staleRamp !== current) {
      this.staleRamp.elevationTexture?.destroy();
      this.staleRamp.colorTexture?.destroy();
    }
    this.staleRamp = current ?? null;
    map.setPaintProperty(this.layerId!, "color-relief-color", color);
  }

  private enqueue(task: () => Promise<void>): Promise<void> {
    this.queue = this.queue.then(task).catch((err) => {
      console.warn(`[${this.prefix}] overlay update failed`, err);
    });
    return this.queue;
  }

  private remove(map: MlMap): void {
    if (this.onMoveEnd) map.off("moveend", this.onMoveEnd);
    this.onMoveEnd = null;
    if (this.layerId && map.getLayer(this.layerId)) map.removeLayer(this.layerId);
    this.layerId = null;
  }

  private async install(map: MlMap): Promise<void> {
    const packs = await ensureDemSources(map);
    this.remove(map);
    if (packs.length === 0 || !this.color) return;
    this.packs = packs;
    this.onMoveEnd = () => this.bindPackForView(map);
    map.on("moveend", this.onMoveEnd);
    this.bindPackForView(map);
  }

  /** Swaps the layer to another pack only when the view needs a different one. */
  private bindPackForView(map: MlMap): void {
    const b = map.getBounds();
    const pack = pickReliefPack(this.packs, [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()], map.getZoom());
    if (!pack || !this.color) return;
    const layerId = `${this.prefix}:${pack.manifest.id}`;
    if (layerId === this.layerId && map.getLayer(layerId)) return;
    if (this.layerId && map.getLayer(this.layerId)) map.removeLayer(this.layerId);
    map.addLayer(
      {
        id: layerId,
        type: "color-relief",
        source: sourceIdFor(pack),
        paint: { "color-relief-color": this.color },
      },
      firstLayerAboveBasemap(map)
    );
    this.layerId = layerId;
  }
}
